import { createEmptyFilter } from '@/common/components/FiltersDrawer/helpers'
import RegionFilter from '@/common/components/RegionFilter/RegionFilter'

import { filterNames } from '../constants'
import AlertsFilterSelector from './AlertsFilterSelector/AlertsFilterSelector'
import CategoryFilter from './CategoryFilter/CategoryFilter'
import CountryFilterSelector from './CountryFilterSelector/CountryFilterSelector'
import ExportPriceFilter from './ExportPriceFilter/ExportPriceFilter'
import ProductStatusFilter from './ProductStatusFilter/ProductStatusFilter'
import UnitCostFilter from './UnitCostFilter/UnitCostFilter'
import WeightFilter from './WeightFilter/WeightFilter'
import { filterTypes } from './constants'

const rangeFilterConfig = {
  [filterNames.weightLte]: { gte: filterNames.weightGte, lte: filterNames.weightLte },
  [filterNames.unitCostLte]: { gte: filterNames.unitCostGte, lte: filterNames.unitCostLte },
}

const rangeFilterNames = Object.keys(rangeFilterConfig)

const arrayFilterNames = [filterNames.hasExportPrice, filterNames.exportPriceCountry]

const mapExportPriceCountry = region => {
  return { id: region.id, code: region.code, name: region.name, continent: region.continent }
}

const getExportPriceCountries = regions => regions.map(mapExportPriceCountry)

export const getRegionIdsByCodes = (codes, regions) => {
  const selectedRegions = regions.filter(region => codes.includes(region.code))

  return selectedRegions.map(region => region.id)
}

const getRegionCodeById = (id, regions) => regions.find(region => region.id === id).code

export const getRegionCodesByIds = (ids, regions) => ids.map(id => getRegionCodeById(id, regions))

const hasValue = value => value != null && value !== ''

const isValueActive = (type, valueMap) => {
  if (rangeFilterNames.includes(type)) {
    const config = rangeFilterConfig[type]
    return hasValue(valueMap[config.gte]) || hasValue(valueMap[config.lte])
  }

  const value = valueMap[type]
  if (Array.isArray(value)) return value.length > 0
  return hasValue(value)
}

const buildValueFromFilters = (type, filters) => {
  if (rangeFilterNames.includes(type)) {
    const config = rangeFilterConfig[type]
    return { [config.gte]: filters[config.gte] ?? null, [config.lte]: filters[config.lte] ?? null }
  }

  return { [type]: filters[type] }
}

export const getDefaultFilterValue = type => {
  if (rangeFilterNames.includes(type)) {
    const config = rangeFilterConfig[type]
    return { [config.gte]: null, [config.lte]: null }
  }

  if (arrayFilterNames.includes(type)) return { [type]: [] }

  return { [type]: '' }
}

export const getInitialFilters = filters => {
  const initialFilters = filterTypes
    .filter(ft => isValueActive(ft.value, filters))
    .map(ft => ({
      id: crypto.randomUUID(),
      type: ft.value,
      value: buildValueFromFilters(ft.value, filters),
    }))

  return initialFilters.length > 0 ? initialFilters : [createEmptyFilter()]
}

export const isFilterValid = filter => {
  if (!filter.type) return true

  return isValueActive(filter.type, filter.value)
}

export const getFilterLabel = filter => {
  return filterTypes?.find(ft => ft.value === filter.type)?.label
}

export const applyFilters = (filters, onChangeFilter) => {
  for (const filter of filters) {
    const { type, value } = filter

    if (rangeFilterNames.includes(type)) {
      const config = rangeFilterConfig[type]
      onChangeFilter(config.gte, value[config.gte] ?? null, false)
      onChangeFilter(config.lte, value[config.lte] ?? null, false)
      continue
    }

    onChangeFilter(type, value[type], false)
  }
}

export const clearAllFilters = onChangeFilter => {
  onChangeFilter(filterNames.missing, null, false)
  onChangeFilter(filterNames.country, null, false)
  onChangeFilter(filterNames.weightGte, null, false)
  onChangeFilter(filterNames.weightLte, null, false)
  onChangeFilter(filterNames.unitCostGte, null, false)
  onChangeFilter(filterNames.unitCostLte, null, false)
  onChangeFilter(filterNames.category, null, false)
  onChangeFilter(filterNames.productStatus, null, false)
  onChangeFilter(filterNames.hasExportPrice, null, false)
  onChangeFilter(filterNames.exportPriceCountry, null, false)
}

export const filterFields = {
  [filterNames.missing]: ({ value, onChangeValue }) => (
    <AlertsFilterSelector value={value.missing} onChange={v => onChangeValue({ missing: v })} />
  ),
  [filterNames.country]: ({ value, onChangeValue, fieldProps }) => (
    <CountryFilterSelector
      regions={fieldProps.regions}
      value={value.country}
      onChange={v => onChangeValue({ country: v })}
    />
  ),
  [filterNames.weightLte]: ({ value, onChangeValue }) => (
    <WeightFilter
      valueGte={value.weightGte}
      onChangeGte={v => onChangeValue({ weightGte: v })}
      valueLte={value.weightLte}
      onChangeLte={v => onChangeValue({ weightLte: v })}
    />
  ),
  [filterNames.unitCostLte]: ({ value, onChangeValue }) => (
    <UnitCostFilter
      valueGte={value.unitCostGte}
      onChangeGte={v => onChangeValue({ unitCostGte: v })}
      valueLte={value.unitCostLte}
      onChangeLte={v => onChangeValue({ unitCostLte: v })}
    />
  ),
  [filterNames.category]: ({ value, onChangeValue, fieldProps }) => (
    <CategoryFilter
      productCategories={fieldProps.productCategories}
      value={value.category}
      onChange={v => onChangeValue({ category: v })}
    />
  ),
  [filterNames.productStatus]: ({ value, onChangeValue }) => (
    <ProductStatusFilter value={value.productStatus} onChange={v => onChangeValue({ productStatus: v })} />
  ),
  [filterNames.hasExportPrice]: ({ value, onChangeValue }) => {
    return <ExportPriceFilter value={value.hasExportPrice} onChange={v => onChangeValue({ hasExportPrice: v })} />
  },
  [filterNames.exportPriceCountry]: ({ value, onChangeValue, fieldProps }) => {
    if (!fieldProps.regions.length) return null

    return (
      <RegionFilter
        label="Países"
        regions={getExportPriceCountries(fieldProps.regions)}
        value={getRegionIdsByCodes(value.exportPriceCountry, fieldProps.regions)}
        onChange={v => onChangeValue({ exportPriceCountry: getRegionCodesByIds(v, fieldProps.regions) })}
      />
    )
  },
}
